import { api } from './client';

/**
 * The typed Personal-Area client: FIVE routes and no sixth.
 *
 * It mirrors the wire structs in `backend/internal/personalarea/handlers.go`, which are
 * themselves pinned to `api/openapi.yaml` by `wire_spec_test.go`. If a field changes it
 * changes there first, then here.
 *
 *   POST /pa/auth/login     — subscriber login + password → the session COOKIE
 *   POST /pa/auth/logout    — end this session (idempotent)
 *   GET  /pa/auth/me        — who am I, re-read live on every call
 *   POST /pa/auth/password  — change my own password (current password required)
 *   POST /pa/auth/recover   — PUBLIC: rotate and send to a matching contact
 *
 * ⚠ THERE IS NO TOKEN IN ANY RESPONSE, AND THAT IS NOT AN OMISSION. The session travels
 * in an HttpOnly cookie the script cannot read (handlers.go: «a token in a JSON body is
 * one XSS away from being read by a script»). So this module stores nothing, and
 * `src/storage.test.ts` fails if any file in this app learns to.
 */

/** The identity the header renders from — `PASubscriber` in the OpenAPI document. */
export interface PASubscriber {
  id: number;
  login: string;
  /** May be empty; the portal falls back to the login. */
  display_name: string;
  /** `subscriber.password_temporary` — the portal PROMPTS, the server does not enforce. */
  must_change_password: boolean;
  /** `subscriber.pa_ip_restricted` — reported so an authenticated caller can be told why. */
  ip_restricted: boolean;
}

/** `PASession` — the login result. */
export interface PASession {
  status: string;
  expires_at: string;
  subscriber: PASubscriber;
}

/** `PAPrincipal` — GET /pa/auth/me. */
export interface PAPrincipal {
  subscriber: PASubscriber;
  expires_at: string;
}

/** `PAStatus` — the flat acknowledgement of logout, password change and recovery. */
export interface PAStatus {
  status: string;
}

export function login(loginName: string, password: string): Promise<PASession> {
  return api.post<PASession>('/pa/auth/login', { login: loginName, password });
}

export function logout(): Promise<PAStatus> {
  return api.post<PAStatus>('/pa/auth/logout');
}

export function me(): Promise<PAPrincipal> {
  return api.get<PAPrincipal>('/pa/auth/me');
}

/**
 * changePassword sends the current and the new password — and NOT the confirmation
 * field the screen shows. The server's own comment says why: «a server that receives
 * two copies of a new password can only compare them, which the browser already did».
 *
 * ⚠ The NEW value is NOT validated here. Questionnaire C18's character set is enforced
 * by `internal/users` and reported as `pa.password_charset` with a Russian message; a
 * second copy of that rule in this file is a rule that will diverge from the one that
 * has to hold. The screen shows what the server said.
 */
export function changePassword(currentPassword: string, newPassword: string): Promise<PAStatus> {
  return api.post<PAStatus>('/pa/auth/password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

/**
 * recover asks for a NEW password to be minted and sent (P7-A2, rotate-and-send).
 *
 * ⚠ Its 200 means «the request was accepted» and NOTHING about whether anything was
 * sent. Both fields are required by the server, and the contact must already be on the
 * card: it is a second thing an attacker must know, not a delivery address the caller
 * chooses. The screen renders ONE wording for every outcome.
 */
export function recover(loginName: string, contact: string): Promise<PAStatus> {
  return api.post<PAStatus>('/pa/auth/recover', { login: loginName, contact });
}
